<div class="min-h-[80vh] flex flex-col items-center gap-6 px-4 py-16 bg-station-cream">
  <div class="w-full max-w-md bg-white rounded-xl shadow-lg p-8">
    <h1 class="text-3xl font-bold mb-2 text-flap-black">My profile</h1>
    <p class="text-rail-gray mb-8">
      {{ hasPassword() ? 'Update your display name or change your password.' : 'Update your display name.' }}
    </p>

    <form (ngSubmit)="submit()" class="space-y-5">
      <div>
        <div class="flex items-center justify-between mb-1">
          <label for="email" class="block text-sm font-semibold text-flap-black">Email</label>
          @if (user()?.emailVerified) {
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-flap-yellow/20 text-xs font-semibold text-flap-black">
              <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd"/>
              </svg>
              Verified
            </span>
          } @else {
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-signal-orange/10 text-xs font-semibold text-signal-orange">
              <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
              </svg>
              Unverified
            </span>
          }
        </div>
        <input
          id="email"
          type="email"
          disabled
          [value]="user()?.email ?? ''"
          class="w-full px-4 py-3 rounded-lg border-2 border-border-subtle bg-bg-secondary text-rail-gray cursor-not-allowed"
        />
        @if (!user()?.emailVerified) {
          <div class="mt-2 text-xs">
            @if (resendSent()) {
              <p class="px-3 py-2 rounded-lg bg-flap-yellow/20 border-2 border-flap-yellow text-flap-black">
                Verification email sent. Check your inbox (and spam folder).
              </p>
            } @else {
              <button
                type="button"
                (click)="resendVerification()"
                [disabled]="resending()"
                class="text-signal-orange font-semibold underline hover:no-underline disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
              >
                {{ resending() ? 'Sending…' : 'Resend verification email' }}
              </button>
            }
            @if (resendError(); as err) {
              <p class="mt-1 text-signal-orange">{{ err }}</p>
            }
          </div>
        }
        @if (user()?.image) {
          <p class="mt-2 text-xs text-rail-gray">
            Your profile picture comes from the account you signed in with. Change it there.
          </p>
        } @else {
          <p class="mt-2 text-xs text-rail-gray">
            Your profile picture comes from
            <a href="https://gravatar.com" target="_blank" rel="noopener" class="text-signal-orange font-semibold hover:underline">Gravatar</a>,
            matched to this email address. Set or change it there.
          </p>
        }
      </div>

      <div>
        <label for="name" class="block text-sm font-semibold text-flap-black mb-1">Display name</label>
        <input
          id="name"
          type="text"
          autocomplete="name"
          required
          maxlength="100"
          [ngModel]="name()"
          (ngModelChange)="name.set($event)"
          name="name"
          class="w-full px-4 py-3 rounded-lg border-2 border-border-subtle focus:border-signal-orange outline-none transition-colors"
        />
      </div>

      @if (hasPassword()) {
        <fieldset class="border-t-2 border-border-subtle pt-5">
          <legend class="sr-only">Change password</legend>
          <p class="text-sm font-semibold text-flap-black mb-1">Change password</p>
          <p class="text-xs text-rail-gray mb-4">Leave blank to keep your current password.</p>

          <div class="space-y-5">
            <div>
              <label for="current-password" class="block text-sm font-semibold text-flap-black mb-1">Current password</label>
              <input
                id="current-password"
                type="password"
                autocomplete="current-password"
                [required]="newPassword().length > 0"
                [ngModel]="currentPassword()"
                (ngModelChange)="currentPassword.set($event)"
                name="currentPassword"
                class="w-full px-4 py-3 rounded-lg border-2 border-border-subtle focus:border-signal-orange outline-none transition-colors"
              />
            </div>

            <div>
              <label for="new-password" class="block text-sm font-semibold text-flap-black mb-1">New password</label>
              <input
                id="new-password"
                type="password"
                autocomplete="new-password"
                minlength="8"
                [ngModel]="newPassword()"
                (ngModelChange)="newPassword.set($event)"
                name="newPassword"
                class="w-full px-4 py-3 rounded-lg border-2 border-border-subtle focus:border-signal-orange outline-none transition-colors"
              />
            </div>

            <div>
              <label for="confirm-password" class="block text-sm font-semibold text-flap-black mb-1">Verify new password</label>
              <input
                id="confirm-password"
                type="password"
                autocomplete="new-password"
                [required]="newPassword().length > 0"
                [ngModel]="confirmPassword()"
                (ngModelChange)="confirmPassword.set($event)"
                name="confirmPassword"
                class="w-full px-4 py-3 rounded-lg border-2 border-border-subtle focus:border-signal-orange outline-none transition-colors"
              />
            </div>
          </div>
        </fieldset>
      }

      @if (error(); as err) {
        <p class="text-sm text-signal-orange">{{ err }}</p>
      }
      @if (success(); as msg) {
        <div class="px-4 py-3 rounded-lg bg-flap-yellow/20 border-2 border-flap-yellow text-flap-black text-sm">
          {{ msg }}
        </div>
      }

      <button
        type="submit"
        [disabled]="submitting()"
        class="w-full px-6 py-3 bg-signal-orange text-white rounded-lg font-semibold hover:bg-signal-orange/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
      >
        {{ submitting() ? 'Saving…' : 'Save changes' }}
      </button>
    </form>
  </div>

  <!-- Danger zone -->
  <div class="w-full max-w-md bg-white rounded-xl shadow-lg p-8 border-2 border-signal-orange">
    <h2 class="text-xl font-bold text-signal-orange mb-2">Danger zone</h2>
    <p class="text-sm text-rail-gray mb-4">
      Permanently delete your account, along with every board and message you have created.
      This cannot be undone.
    </p>
    <button
      type="button"
      (click)="openDeleteModal()"
      class="px-4 py-2 bg-transparent border-2 border-signal-orange text-signal-orange rounded-lg font-semibold text-sm hover:bg-signal-orange hover:text-white transition-all cursor-pointer"
    >
      Delete account
    </button>
  </div>
</div>

@if (showDeleteModal()) {
  <app-delete-account-modal
    [busy]="deleting()"
    [error]="deleteError()"
    [requirePassword]="hasPassword() === true"
    (cancelled)="closeDeleteModal()"
    (confirmed)="confirmDelete($event)"
  />
}
